import {
  CheckboxWithLabel,
  CountrySelectorForHeader,
  InputWithLabel,
  SelectWithLabel
} from '@adotel/components'
import { useQuery } from '@apollo/client'
import { MenuItem } from '@material-ui/core'
import { FormikErrors, FormikTouched } from 'formik'
import React, { useState } from 'react'
import { useParams } from 'react-router-dom'

import SectionTitle from '../../components/SectionTitle'
import { CHANNEL_TYPES_QUERY } from '../../graphql/channelTypes'
import { CountryType, countries } from '../../helpers/countriesList'
import { entityCodeRegex } from '../../helpers/regex'
import { getCurrenciesAvailable } from '../../helpers/userData'
import { getTranslate, lang } from '../../localization'
import { Channel } from '../../types/channel'
import { ChannelTypeDetails } from '../../types/channelType'

interface ChannelBasicDetailsProps {
  values: Channel
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void
  errors: FormikErrors<Channel>
  touched: FormikTouched<Channel>
  handleBlur: any
  isSubmitting: any
  classes: any
}

export const ChannelBasicDetails: React.FC<ChannelBasicDetailsProps> = ({
  values,
  setFieldValue,
  errors,
  touched,
  handleBlur,
  isSubmitting,
  classes
}) => {
  const params = useParams<{ id: string }>()
  const [selectedCountryForName, setselectedCountryForName] =
    useState<CountryType>(countries[0])

  const { data: channelTypes, loading: loadingForChannelTypes } = useQuery<{
    ChannelTypes: ChannelTypeDetails[]
  }>(CHANNEL_TYPES_QUERY)

  const nameLanguage =
    selectedCountryForName?.nativeLanguage && lang !== 'en'
      ? selectedCountryForName.nativeLanguage
      : selectedCountryForName?.language

  return (
    <>
      <div className={classes.titleContainer}>
        <SectionTitle title="Basic Details" marginBottom />
      </div>{' '}
      <div className={classes.fieldsContainer}>
        <InputWithLabel
          data-cy="channelName"
          name="basicDetails.name"
          label={getTranslate('Name')}
          placeholder={getTranslate('Enter the channel name')}
          autoComplete="off"
          value={values?.basicDetails?.name}
          onChange={(event) =>
            setFieldValue('basicDetails.name', event.currentTarget.value)
          }
          error={
            (touched.basicDetails?.name && !!errors.basicDetails?.name) ||
            undefined
          }
          helpertext={
            touched.basicDetails?.name
              ? getTranslate(errors.basicDetails?.name || '')
              : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
        />
        <InputWithLabel
          data-cy="channelName2"
          name="basicDetails.name2"
          label={getTranslate('Name2')}
          placeholder={getTranslate('Enter the channel Name2')}
          autoComplete="off"
          value={values?.basicDetails?.name2}
          onChange={(event) =>
            setFieldValue('basicDetails.name2', event.currentTarget.value)
          }
          error={
            (touched.basicDetails?.name2 && !!errors.basicDetails?.name2) ||
            undefined
          }
          helpertext={
            touched.basicDetails?.name2
              ? getTranslate(errors.basicDetails?.name2 || '')
              : undefined
          }
          style={{ fontFamily: 'IranSans' }}
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
        />
        <InputWithLabel
          data-cy="channelCode"
          name="basicDetails.code"
          label={getTranslate('Code')}
          placeholder={getTranslate('Choose a code for channel')}
          autoComplete="off"
          value={values?.basicDetails?.displayChannelCode}
          onChange={(event) => {
            const rawInput = event.currentTarget.value
            const inputValue = rawInput.replace(/\s+/g, '') // remove all whitespace (including spaces, tabs)

            if (entityCodeRegex.test(inputValue) || inputValue === '') {
              setFieldValue('basicDetails.code', inputValue)
              setFieldValue('basicDetails.displayChannelCode', inputValue)
            }
          }}
          error={
            (touched.basicDetails?.code && !!errors.basicDetails?.code) ||
            undefined
          }
          helpertext={
            touched.basicDetails?.code
              ? getTranslate(errors.basicDetails?.code || '')
              : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
        />
        <InputWithLabel
          data-cy="channelIATA"
          name="basicDetails.IATA"
          label={getTranslate('IATA')}
          placeholder={getTranslate('Choose an IATA for channel')}
          autoComplete="off"
          value={values?.basicDetails?.IATA}
          onChange={(event) =>
            setFieldValue('basicDetails.IATA', event.currentTarget.value)
          }
          error={
            (touched.basicDetails?.IATA && !!errors.basicDetails?.IATA) ||
            undefined
          }
          helpertext={
            touched.basicDetails?.IATA
              ? getTranslate(errors.basicDetails?.IATA || '')
              : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
        />
        <SelectWithLabel
          data-cy="channelChannelType"
          disabled={!!params.id}
          name="basicDetails.channelType"
          id="basicDetails.channelType"
          fullWidth
          value={
            values?.basicDetails?.channelType
              ? values?.basicDetails?.channelType
              : ''
          }
          onChange={(event) => {
            setFieldValue('basicDetails.channelType', event.target.value)
          }}
          size="small"
          error={
            (touched.basicDetails?.channelType &&
              !!errors.basicDetails?.channelType) ||
            undefined
          }
          helpertext={
            touched.basicDetails?.channelType
              ? getTranslate(errors.basicDetails?.channelType || '')
              : undefined
          }
          label={getTranslate('Channel Type')}>
          {!loadingForChannelTypes &&
          channelTypes &&
          channelTypes?.ChannelTypes ? (
            channelTypes.ChannelTypes.map((channelType, index) => (
              <MenuItem
                data-cy={index == 0 ? 'firstChannelType' : null}
                key={channelType.Name}
                value={channelType.Name}>
                {channelType.Name}
              </MenuItem>
            ))
          ) : (
            <MenuItem key={'loading ...'} value={'loading ...'}>
              {getTranslate('loading ...')}
            </MenuItem>
          )}
        </SelectWithLabel>
        <SelectWithLabel
          data-cy="channelChannelCurrency"
          name="basicDetails.currencys"
          id="basicDetails.currencys"
          fullWidth
          value={
            values?.basicDetails?.currency
              ? values?.basicDetails?.currency?.toString().toUpperCase()
              : ''
          }
          onChange={(event) =>
            setFieldValue('basicDetails.currency', event.target.value)
          }
          size="small"
          error={
            (touched.basicDetails?.currency &&
              !!errors.basicDetails?.currency) ||
            undefined
          }
          helpertext={
            touched.basicDetails?.currency
              ? getTranslate(errors.basicDetails?.currency || '')
              : undefined
          }
          label={getTranslate('Channel Currency')}>
          {getCurrenciesAvailable().map((currency, index) => (
            <MenuItem
              data-cy={index == 0 ? 'firstChannelCurrency' : null}
              key={currency}
              value={currency}>
              {currency}
            </MenuItem>
          ))}
        </SelectWithLabel>
        <InputWithLabel
          name="basicDetails.shortName"
          label={getTranslate('Short Name')}
          placeholder={getTranslate('Enter the channel short name')}
          autoComplete="off"
          value={values?.basicDetails?.shortName}
          onChange={(event: { currentTarget: { value: any } }) =>
            setFieldValue('basicDetails.shortName', event.currentTarget.value)
          }
          error={
            touched.basicDetails?.shortName && !!errors.basicDetails?.shortName
          }
          helpertext={
            touched.basicDetails?.shortName
              ? getTranslate(errors.basicDetails?.shortName || '')
              : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
        />
        <InputWithLabel
          label={`${lang === 'en' ? nameLanguage : ''} ${getTranslate(
            'Short Name'
          )} ${lang === 'fa' ? nameLanguage : ''}`}
          placeholder={`${getTranslate(
            'Enter the channel short name in'
          )} ${nameLanguage}`}
          autoComplete="off"
          id={`basicDetails.shortNameTranslatedList[${values?.basicDetails?.shortNameTranslatedList.findIndex(
            (item) => item.label === selectedCountryForName.label
          )}].value`}
          name={`basicDetails.shortNameTranslatedList[${values?.basicDetails?.shortNameTranslatedList.findIndex(
            (item) => item.label === selectedCountryForName.label
          )}].value`}
          value={
            values?.basicDetails?.shortNameTranslatedList.find(
              (item) => item.label === selectedCountryForName.label
            )?.value
          }
          style={{ fontFamily: 'IranSans' }}
          onChange={(event: {
            currentTarget: { value: React.SetStateAction<string> }
          }) => {
            setFieldValue(
              `basicDetails.shortNameTranslatedList[${values?.basicDetails?.shortNameTranslatedList.findIndex(
                (item) => item.label === selectedCountryForName.label
              )}].value`,
              event.currentTarget.value
            )
          }}
          error={
            (touched.basicDetails?.shortNameTranslatedList &&
              !values?.basicDetails?.shortNameTranslatedList[0].value) ||
            undefined
          }
          helpertext={
            touched.basicDetails?.shortNameTranslatedList &&
            !values?.basicDetails?.shortNameTranslatedList[0].value
              ? getTranslate('This field is required')
              : ''
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
          rightHeader={
            <CountrySelectorForHeader
              selectedCountry={selectedCountryForName}
              onChange={(item: CountryType) => {
                const translatedName = {
                  label: item.label,
                  nativeLabel: item.nativeLanguage,
                  value: ''
                }
                const tempTranslatedArr = [
                  ...values?.basicDetails?.shortNameTranslatedList,
                  values?.basicDetails?.shortNameTranslatedList.find(
                    (singleName) => singleName.label === item.label
                  )
                    ? undefined
                    : translatedName
                ]
                setFieldValue(
                  'basicDetails.shortNameTranslatedList',
                  tempTranslatedArr
                )
                setselectedCountryForName(item)
              }}
            />
          }
        />
        <div className={classes.showProviderContainer}>
          <CheckboxWithLabel
            name="basicDetails.showProviderName"
            placeholder={getTranslate('Show Provider Name')}
            value={values.basicDetails.showProviderName}
            checked={values.basicDetails.showProviderName}
            onChange={(event) =>
              setFieldValue(
                'basicDetails.showProviderName',
                event.currentTarget.checked
              )
            }
            error={
              (touched.basicDetails?.showProviderName &&
                !!errors.basicDetails?.showProviderName) ||
              undefined
            }
            helpertext={
              touched.basicDetails?.showProviderName
                ? getTranslate(errors.basicDetails?.showProviderName || '')
                : undefined
            }
            onBlur={handleBlur}
            disabled={isSubmitting}
            inputSize="small"
          />
        </div>
      </div>
    </>
  )
}
